import { currentAccountId } from './account'

/**
 * WHAT IS ON THE SHELF, AND HOW IT GOT THERE.
 *
 * INV-01, and the part of INV-02 that can be true without a scanner.
 *
 * ON HAND IS DERIVED, NEVER STORED. Same rule the boards in this product
 * already follow: a stored count is a number that can disagree with what
 * happened, and stock is the place where that disagreement costs a Saturday.
 * So the record is a ledger of movements and every count on every screen is a
 * walk over it. A correction is another movement with a reason on it, which is
 * what the spec means by adjustments require reason and ledger history, and it
 * is the only version of that which cannot be quietly edited away.
 *
 * NO COUNTS MEANS UNKNOWN, NOT ZERO. Straight out of INV-01 and the single
 * most important line in it. An item nobody has ever counted is not out of
 * stock; a screen that said zero would have somebody buying four hundred bulbs
 * they already own, and a shortage check that treated it as zero would block
 * every job in the season. countedFor returns null and every reader prints the
 * word unknown.
 *
 * NO BLIND NEGATIVE INVENTORY. INV-02. A movement that would take a location
 * below zero is refused with the number it would have gone to, rather than
 * being written and left for somebody to find in February.
 *
 * WHAT IS DELIBERATELY NOT HERE. Reservations are not stock: MAT-03 is
 * explicit that a reservation is neither loaded nor installed, so it is a
 * fourth state with an idempotent operation key behind it and there is nothing
 * to key it on yet. Scanning needs hardware. Receipt extraction needs a model
 * and a document store. All three are named on the screen.
 */

/** Where a thing physically is. Two, because a shop has a shelf and a truck. */
export type Place = 'warehouse' | 'truck'

export const PLACES: { id: Place; label: string }[] = [
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'truck', label: 'On the truck' },
]

/**
 * WHY IT MOVED, and every one of these is a real event somebody can name.
 *
 * received: it arrived from a supplier.
 * loaded: warehouse to truck.
 * returned: truck back to warehouse.
 * used: it went onto a house and is not coming back this season.
 * damaged: it broke. Off the count, and the reason is kept.
 * counted: a physical count, which corrects the ledger rather than replacing it.
 */
export type MoveKind = 'received' | 'loaded' | 'returned' | 'used' | 'damaged' | 'counted'

export const MOVE_KINDS: { id: MoveKind; label: string; from: Place | null; to: Place | null }[] = [
  { id: 'received', label: 'Arrived from a supplier', from: null, to: 'warehouse' },
  { id: 'loaded', label: 'Loaded onto the truck', from: 'warehouse', to: 'truck' },
  { id: 'returned', label: 'Back off the truck', from: 'truck', to: 'warehouse' },
  { id: 'used', label: 'Went onto a house', from: 'truck', to: null },
  { id: 'damaged', label: 'Broken or written off', from: 'warehouse', to: null },
  { id: 'counted', label: 'Counted on the shelf', from: null, to: null },
]

export interface Movement {
  id: string
  itemId: string
  kind: MoveKind
  /** Always positive. The kind decides which way it goes. */
  qty: number
  /** ISO yyyy-mm-dd. The day it happened, not the day it was typed. */
  day: string
  /** Free text. Required on a correction, optional on everything else. */
  why: string
  /** Which job it went onto, when the kind is used. Empty otherwise. */
  jobId: string
  at: string
}

export interface CatalogItem {
  id: string
  name: string
  /** The shop's own part number. Free text: every supplier numbers differently. */
  sku: string
  /** Bulbs, clips, feet, boxes. What one of it is. */
  unit: string
  /** How many come in a box, for ordering. Zero when it is not boxed. */
  packSize: number
  active: boolean
}

export interface Counts {
  warehouse: number
  truck: number
  /** Everything that has left: used on houses plus written off. */
  gone: number
}

const keyFor = (what: string) => `lightsos.${what}.${currentAccountId()}`

/**
 * THE COUNT, WALKED FROM THE LEDGER.
 *
 * A physical count is not a replacement for the history: it is a movement that
 * says the shelf actually held this many on this day, so everything before it
 * is history and everything after it is arithmetic on top. That is why counted
 * resets the running warehouse figure rather than adding to it.
 */
export function countsFor(itemId: string, moves: Movement[]): Counts | null {
  const mine = moves
    .filter((m) => m.itemId === itemId)
    .slice()
    .sort((a, b) => (a.day === b.day ? (a.at < b.at ? -1 : 1) : a.day < b.day ? -1 : 1))
  // Nobody has ever counted or received it. Unknown, and never zero.
  if (mine.length === 0) return null
  let warehouse = 0
  let truck = 0
  let gone = 0
  for (const m of mine) {
    const q = Math.max(0, Math.round(m.qty))
    switch (m.kind) {
      case 'received':
        warehouse += q
        break
      case 'loaded':
        warehouse -= q
        truck += q
        break
      case 'returned':
        truck -= q
        warehouse += q
        break
      case 'used':
        truck -= q
        gone += q
        break
      case 'damaged':
        warehouse -= q
        gone += q
        break
      case 'counted':
        warehouse = q
        break
    }
  }
  return { warehouse, truck, gone }
}

export const onHand = (c: Counts | null): number | null =>
  c === null ? null : c.warehouse + c.truck

/**
 * WOULD THIS MOVEMENT TAKE SOMETHING BELOW ZERO.
 *
 * INV-02: no blind negative inventory. Returns the sentence to show, or null
 * when the movement is fine. It names the number it would have gone to,
 * because minus fourteen clips is a fact somebody can act on and "not enough"
 * is not.
 */
export function refuse(m: Omit<Movement, 'id' | 'at'>, moves: Movement[]): string | null {
  const q = Math.round(m.qty)
  if (!Number.isFinite(q) || q < 0) return 'How many is not a number.'
  /*
   * COUNTING ZERO IS THE WHOLE POINT OF COUNTING.
   *
   * I looked and the shelf is empty is the sentence that turns unknown into a
   * fact, and it is the difference this entire file exists to keep. Every
   * other kind needs a number above zero; a count does not.
   */
  if (m.kind === 'counted') return null
  if (q === 0) return 'How many is not a number above zero.'
  const c = countsFor(m.itemId, moves)
  // Nothing counted yet: receiving is how a count starts, anything else is
  // moving something nobody knows the shop has.
  if (c === null) {
    return m.kind === 'received'
      ? null
      : 'Nothing has been counted or received for this yet, so there is nothing to move.'
  }
  const spec = MOVE_KINDS.find((k) => k.id === m.kind)
  const from = spec?.from
  if (!from) return null
  const have = from === 'warehouse' ? c.warehouse : c.truck
  if (q > have) {
    const place = from === 'warehouse' ? 'the warehouse' : 'the truck'
    return `That takes ${place} to ${have - q}. There ${have === 1 ? 'is' : 'are'} ${have} on it.`
  }
  return null
}

/** What a job's approved pick list needs that the shop does not have. */
export interface Short {
  itemId: string
  name: string
  need: number
  have: number | null
}

/**
 * CAN THIS JOB BE LOADED.
 *
 * Takes what the job needs keyed by catalog item, because this file has no
 * business knowing what a bulb is. An item nobody has counted comes back with
 * have: null and the screen says unknown rather than short: refusing to load a
 * van over a number nobody has ever entered is how a stock feature gets turned
 * off in week two.
 */
export function shortfall(
  need: { itemId: string; qty: number }[],
  catalog: CatalogItem[],
  moves: Movement[],
): Short[] {
  const out: Short[] = []
  for (const n of need) {
    // Needing none of something is not a shortage of it, whatever the shelf
    // says, and an unknown count on a line nobody needs is not worth a row.
    if (!Number.isFinite(n.qty) || n.qty <= 0) continue
    const item = catalog.find((c) => c.id === n.itemId)
    if (!item) continue
    const have = onHand(countsFor(n.itemId, moves))
    if (have === null || have < n.qty) {
      out.push({ itemId: n.itemId, name: item.name, need: n.qty, have })
    }
  }
  return out
}

/* --------------------------------------------------------------- storage */

export function normalizeCatalog(raw: unknown): CatalogItem[] {
  if (!Array.isArray(raw)) return []
  const out: CatalogItem[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const m = r as Partial<CatalogItem>
    if (typeof m.id !== 'string' || !m.id) continue
    out.push({
      id: m.id,
      name: typeof m.name === 'string' ? m.name : '',
      sku: typeof m.sku === 'string' ? m.sku : '',
      unit: typeof m.unit === 'string' && m.unit ? m.unit : 'each',
      packSize: Number.isFinite(m.packSize) ? Math.max(0, Math.round(Number(m.packSize))) : 0,
      active: m.active !== false,
    })
  }
  return out
}

export function normalizeMoves(raw: unknown): Movement[] {
  if (!Array.isArray(raw)) return []
  const out: Movement[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const m = r as Partial<Movement>
    if (typeof m.id !== 'string' || typeof m.itemId !== 'string') continue
    if (!MOVE_KINDS.some((k) => k.id === m.kind)) continue
    if (!Number.isFinite(m.qty)) continue
    out.push({
      id: m.id,
      itemId: m.itemId,
      kind: m.kind as MoveKind,
      qty: Math.max(0, Math.round(Number(m.qty))),
      day: typeof m.day === 'string' ? m.day.slice(0, 10) : '',
      why: typeof m.why === 'string' ? m.why : '',
      jobId: typeof m.jobId === 'string' ? m.jobId : '',
      at: typeof m.at === 'string' ? m.at : '',
    })
  }
  return out
}

export const loadCatalog = (): CatalogItem[] => {
  try {
    const raw = localStorage.getItem(keyFor('catalog'))
    return raw ? normalizeCatalog(JSON.parse(raw)) : []
  } catch {
    return []
  }
}
export const saveCatalog = (list: CatalogItem[]) => {
  try {
    localStorage.setItem(keyFor('catalog'), JSON.stringify(list))
  } catch {
    /* private mode */
  }
}
export const loadMoves = (): Movement[] => {
  try {
    const raw = localStorage.getItem(keyFor('stock'))
    return raw ? normalizeMoves(JSON.parse(raw)) : []
  } catch {
    return []
  }
}
export const saveMoves = (list: Movement[]) => {
  try {
    localStorage.setItem(keyFor('stock'), JSON.stringify(list))
  } catch {
    /* private mode */
  }
}

export const newItem = (id: string): CatalogItem => ({
  id,
  name: '',
  sku: '',
  unit: 'each',
  packSize: 0,
  active: true,
})

export const NOT_IN_STOCK: string[] = [
  'Reservations. A reservation is not loaded and not installed stock, so it is a fourth state with an operation key behind it that a retry can reuse, and there is nothing to key it on yet.',
  'Scanning. That needs hardware, and searching a list of twenty items is not the bottleneck a scanner solves.',
  'Reading a supplier receipt. That needs a model and somewhere to keep the document.',
  'Cost and margin. Nothing here holds what anything was paid for, and guessing at it would put a wrong number next to a real one.',
]
